import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { tap, share } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Product } from '../model/product';
import { Image } from '../model/image';
import { environment } from '../../environments/environment';
import { convertToQueryString, applyImageFormatting } from '../utils';

export interface StockListResponse {
  result: Product[];
  metadata: { pages: unknown[]; totalCount: number; pageSize: number };
}

export interface ImageListResponse {
  result: Image[];
  metadata: { pages: unknown[]; totalCount: number; pageSize: number };
}

@Injectable({ providedIn: 'root' })
export class StockListService {
  private http = inject(HttpClient);
  private toasterService = inject(ToastrService);


  fetchParams: Record<string, string | number | boolean>;
  stocklist$: Observable<StockListResponse>;
  images$: Observable<ImageListResponse>;

  url = (params: Record<string, string | number | boolean>): string => `${environment.api}/v1/stockitems?${convertToQueryString(params)}`;
  imageUrl = (params: Record<string, string | number | boolean>): string => `${environment.api}/v1/images?${convertToQueryString(params)}`;
  stockItemsUrl = (): string => `${environment.api}/v1/stockitems`;
  stockItemUrl = (stockItemId: string | number): string => `${environment.api}/v1/stockitems/${stockItemId}`;
  stockItemByCodeUrl = (stockItemCode: string): string => `${environment.api}/v1/stockitems/${stockItemCode}`;

  fetch(params: Record<string, string | number | boolean>): Observable<StockListResponse> {
    this.fetchParams = params;
    console.log(`stock-list.service.fetch: ${JSON.stringify(params)}`);
    this.stocklist$ = this.http.get<StockListResponse>(this.url(params)).pipe(
      share(),
      tap((data: StockListResponse) => {
        data.result.forEach(product => {
          Product.enrich(product);
        });
        data.metadata.pages = Array(Math.ceil(data.metadata.totalCount / data.metadata.pageSize));
      })
    );
    return this.stocklist$;
  }

  refetch(): void {
    console.log(`stock-list.service.refetch: ${JSON.stringify(this.fetchParams)}`);
    this.fetch(this.fetchParams);
  }

  fetchStockItemByCode(code): Observable<Product> {
    const url = this.stockItemByCodeUrl(code);
    const result = this.http.get<Product>(url).pipe(
      tap((product: Product) => {
        Product.enrich(product);
      })
    );
    return result;
  }

  fetchImages(term: string): Observable<ImageListResponse> {
    console.log(`stock-list.service.fetchImages: ${term}`);
    this.images$ = this.http.get<ImageListResponse>(this.imageUrl({ term })).pipe(
      share(),
      tap((data: ImageListResponse) => {
        data.result.forEach(image => {
          image.safeLargeUrl = `${environment.image.root}${image.fileName}${environment.image.thumbnail}`;
        });
        data.metadata.pages = Array(Math.ceil(data.metadata.totalCount / data.metadata.pageSize));
      })
    );
    return this.images$;
  }

  deleteStockItem(product: Product): Subscription {
    console.log(`stock-list.service.deleteStockItem: ${JSON.stringify(product)}`);
    return this.http.delete(this.stockItemUrl(product.id))
      .subscribe(() => {
        this.toasterService.success('Stock item deleted.', 'Successful!');
        product.changed = false;
      }, () => {
        product.changed = true;
      });
  }

  createStockItem(product: Product): Subscription {
    console.log(`stock-list.service.cloneStockItem: ${JSON.stringify(product)}`);
    return this.http.post(this.stockItemsUrl(), product)
      .subscribe(() => {
        this.toasterService.success('Stock item created.', 'Successful!');
        product.changed = false;
      }, () => {
        product.changed = true;
      });
  }

  saveStockItem(product: Product, callback = () => {}): Subscription {
    console.log(`stock-list.service.saveStockItem: ${JSON.stringify(product)}`);
    product.price = Math.round(product.priceInEuros * 100);
    product.wholeSalePrice = Math.round(product.wholeSalePriceInEuros * 100);

    return this.http.put<Product>(this.stockItemUrl(product.id), product)
      .subscribe(data => {
        this.toasterService.success('Stock item saved.', 'Successful!');
        product.galleryItemId = data.galleryItemId; // This field may have just been set
        product.slug = data.slug; // This field may have just been set
        applyImageFormatting(product);
        product.changed = false;
        Product.enrich(product);
        callback();
      }, () => {
        product.changed = true;
      });
  }
}
